import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { AllocateInterviewRequest, AllocationService } from './allocation.service';
import { Allocation, Interviewer } from '../../interfaces/allocation/allocation.interface';
import { AllocationSearchParam } from '../../interfaces/allocation/allocation-search-param.interface';
import { PageResponse } from '../../../../core/interfaces/page-response.interface';

@Injectable({
    providedIn: 'root'
})
export class AllocationMockService extends AllocationService {
    private mockAllocations: Allocation[] = [
        {
            uniqueId: "RVXBuERieB",
            date: "20/03/2026",
            startTime: "08:00 PM",
            endTime: "09:00 PM",
            status: "BOOKED",
            careerUniqueId: "CAR456",
            careerName: "Senior Developer",
            assessmentUniqueId: "ASM-001",
            assessmentName: "Online Interview",
            ccmUniqueId: null,
            createdDate: "20/03/2026",
            position: "Full Stack Developer",
            stage: "Round 1"
        },
        {
            uniqueId: "alloc-002",
            date: "21/03/2026",
            startTime: "11:00 AM",
            endTime: "12:00 PM",
            status: "PENDING",
            careerUniqueId: "CAR457",
            assessmentUniqueId: "ASM-002",
            assessmentName: "Aptitude Test",
            careerName: "Backend Engineer",
            position: "Backend Developer",
            stage: "Round 1",
            createdDate: "20/03/2026"
        },
        {
            uniqueId: "alloc-003",
            date: "22/03/2026",
            startTime: "02:00 PM",
            endTime: "03:00 PM",
            status: "SCHEDULED",
            careerUniqueId: "CAR458",
            assessmentUniqueId: "ASM-003",
            assessmentName: "Technical Interview (Online)",
            careerName: "Frontend Specialist",
            position: "UI/UX Developer",
            stage: "Round 2",
            createdDate: "19/03/2026"
        },
        {
            uniqueId: "alloc-004",
            date: "23/03/2026",
            startTime: "09:00 AM",
            endTime: "10:00 AM",
            status: "PENDING",
            careerUniqueId: "CAR459",
            assessmentUniqueId: "ASM-004",
            assessmentName: "Face to Face Interview",
            careerName: "Data Scientist",
            position: "Data Analyst",
            stage: "Round 2",
            createdDate: "21/03/2026"
        },
        {
            uniqueId: "alloc-005",
            date: "24/03/2026",
            startTime: "04:00 PM",
            endTime: "05:00 PM",
            status: "COMPLETED",
            careerUniqueId: "CAR460",
            assessmentUniqueId: "ASM-005",
            assessmentName: "HR Interview",
            careerName: "DevOps Architect",
            position: "Infrastructure Engineer",
            stage: "Final Round",
            createdDate: "15/03/2026"
        }
    ];

    private mockInterviewers: Interviewer[] = [
        {
            id: '1',
            uniqueId: 'int-1',
            name: 'Dr. Sarah Wilson',
            email: 'sarah.wilson@demo.com',
            role: 'Senior Staff'
        },
        {
            id: '2',
            uniqueId: 'int-2',
            name: 'Prof. Michael Brown',
            email: 'michael.brown@demo.com',
            role: 'Dean'
        }
    ];

    override findAll(searchParam: AllocationSearchParam): Observable<PageResponse<Allocation>> {
        let filtered = [...this.mockAllocations];
        if (searchParam.searchTerm) {
            const term = searchParam.searchTerm.toLowerCase();
            filtered = filtered.filter(a => 
                (a.assessmentName && a.assessmentName.toLowerCase().includes(term)) ||
                (a.stage && a.stage.toLowerCase().includes(term)) ||
                (a.careerName && a.careerName.toLowerCase().includes(term)) ||
                (a.position && a.position.toLowerCase().includes(term)) ||
                (a.interviewerName && a.interviewerName.toLowerCase().includes(term))
            );
        }

        const response: PageResponse<Allocation> = {
            content: filtered,
            page: {
                number: searchParam.page || 0,
                size: searchParam.size || 10,
                totalElements: filtered.length,
                totalPages: 1
            }
        };

        return of(response).pipe(delay(500));
    }

    override find(id: string): Observable<Allocation> {
        const item = this.mockAllocations.find(a => a.uniqueId === id);
        return of(item!).pipe(delay(300));
    }

    override create(data: Partial<Allocation>): Observable<Allocation> {
        const newAllocation: Allocation = {
            uniqueId: `alloc-${Math.random().toString(36).substr(2, 9)}`,
            status: 'PENDING',
            ...data
        } as Allocation;
        this.mockAllocations.push(newAllocation);
        return of(newAllocation).pipe(delay(500));
    }

    override update(id: string, data: Partial<Allocation>): Observable<Allocation> {
        const index = this.mockAllocations.findIndex(a => a.uniqueId === id);
        if (index > -1) {
            this.mockAllocations[index] = { ...this.mockAllocations[index], ...data };
            return of(this.mockAllocations[index]).pipe(delay(500));
        }
        throw new Error('Allocation not found');
    }

    override delete(id: string): Observable<void> {
        const index = this.mockAllocations.findIndex(a => a.uniqueId === id);
        if (index > -1) {
            this.mockAllocations.splice(index, 1);
        }
        return of(void 0).pipe(delay(300));
    }

    override getInterviewers(id?: string): Observable<Interviewer[]> {
        return of([...this.mockInterviewers]).pipe(delay(300));
    }

    override allocate(assessmentUniqueId: string, payload: AllocateInterviewRequest[]): Observable<void> {
        console.log('Mock allocate called', { assessmentUniqueId, payload });
        return of(void 0).pipe(delay(500));
    }

    override approve(data: {
        bookingUniqueId: string;
        interviewerUniqueId: string;
        fromTime: string;
        toTime: string;
        date: string;
    }): Observable<void> {
        // Update local mock state
        const index = this.mockAllocations.findIndex(a => a.uniqueId === data.bookingUniqueId);
        if (index > -1) {
            const interviewer = this.mockInterviewers.find(i => i.uniqueId === data.interviewerUniqueId);
            this.mockAllocations[index] = {
                ...this.mockAllocations[index],
                interviewerUniqueId: data.interviewerUniqueId,
                interviewerName: interviewer?.name,
                interviewerEmail: interviewer?.email,
                startTime: data.fromTime,
                endTime: data.toTime,
                date: data.date.includes('-') ? data.date.split('-').reverse().join('/') : data.date,
                status: 'BOOKED'
            };
        }
        return of(void 0).pipe(delay(500));
    }
}
